import { throttle } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { clearApolloCache } from '@/shared/api';
import { EMITTER_CONSTANTS, KEY_RECOIL } from '@/shared/config';
import { useEmitter } from '@/shared/lib/customHooks';
import { selectIsEmptyData, useReportStore } from '@/entities/report';
import { HrChartHandle } from '../hrChart';
import { HrChartOptionsHandle } from '../hrChartOptions';
import type { EcgDisclosureProps } from './index';

/**
 * Backs the ECG disclosure section of the strips management tab. It exposes the empty-data and invalid-time modal state from the report store, holds refs to the heart-rate chart and its date options, forwards arrow clicks to the chart, and on the events-updated and beats-updated emitter signals reloads immediately when the tab is active or defers the reload until it next becomes active.
 *
 * @param props - ECG disclosure props carrying whether the tab is currently active.
 * @returns An object with the empty-data and invalid-time modal flags, the acknowledge and arrow handlers, the chart and chart options refs and the initialised ref.
 */
const useEcgDisclosure = ({ isActive }: Pick<EcgDisclosureProps, 'isActive'>) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;
  const isEmptyData = useReportStore(selectIsEmptyData(keyRecoil));
  const isOpenInvalidTimeModal = useReportStore((s) => s.isOpenInvalidTimeModal);
  const setIsOpenInvalidTimeModal = useReportStore((s) => s.actions.setIsOpenInvalidTimeModal);

  const hrChartRef = useRef<HrChartHandle | null>(null);
  const hrChartOptionsRef = useRef<HrChartOptionsHandle | null>(null);
  const isInitial = useRef(false);
  const willReloadWhenActive = useRef(false);

  /**
   * Forwards a previous or next minute request to the heart-rate chart. It calls through the chart ref only once the chart is mounted.
   *
   * @param isNext - True to move forward one step, false to move back.
   * @returns Nothing.
   */
  const handleClickArrowButton = (isNext = false) => {
    if (hrChartRef.current) {
      hrChartRef.current.handleClickArrowButton(isNext);
    }
  };

  /**
   * Dismisses the invalid time range modal. It clears the modal flag in the report store.
   *
   * @returns Nothing.
   */
  const onClickOkay = () => {
    setIsOpenInvalidTimeModal(false);
  };

  /**
   * Reloads the ECG disclosure date counts after events or beats change elsewhere in the report. It clears the Apollo cache and calls reload on the chart options handle, throttled to a single trailing call per 500 milliseconds and skipped until the tab has been activated at least once.
   *
   * @returns The memoized throttled reload callback.
   */
  const handleReload = useCallback(
    throttle(
      async () => {
        if (hrChartOptionsRef.current && isInitial.current) {
          await clearApolloCache();
          hrChartOptionsRef.current.reload();
        }
      },
      500,
      { leading: false, trailing: true },
    ),
    [],
  );

  useEffect(() => {
    if (isActive) {
      isInitial.current = true;
      if (willReloadWhenActive.current) {
        handleReload();
        willReloadWhenActive.current = false;
      }
    }
  }, [isActive]);

  useEmitter(
    EMITTER_CONSTANTS.EVENTSUPDATED_EVENT,
    async () => {
      if (isActive) {
        handleReload();
      }
      if (!willReloadWhenActive.current) {
        willReloadWhenActive.current = true;
      }
    },
    [isActive],
  );

  useEmitter(
    EMITTER_CONSTANTS.BEATSUPDATED_EVENT,
    () => {
      if (isActive) {
        handleReload();
      }
      if (!willReloadWhenActive.current) {
        willReloadWhenActive.current = true;
      }
    },
    [isActive],
  );

  return {
    isEmptyData,
    isOpenInvalidTimeModal,
    onClickOkay,
    handleClickArrowButton,
    hrChartRef,
    hrChartOptionsRef,
    isInitial,
  };
};

export default useEcgDisclosure;
